import React from 'react';
import { MessageCircle, Phone, Zap, ExternalLink } from 'lucide-react';
interface ActiveAgent {
  id: string;
  name: string;
  category: string;
  description: string;
  accessMethod: string;
  accessLink: string;
  platform: string;
  icon: string;
  accessType: 'web' | 'sms' | 'whatsapp' | 'telegram' | 'app';
}
const SUPER_AGENTS: ActiveAgent[] = [{
  id: 'meta-ai',
  name: 'Meta AI',
  category: 'General / Visual Search',
  description: 'Real-time product comparisons, gift ideas, visual search, general questions.',
  accessMethod: 'Web & Social Apps',
  accessLink: 'https://meta.ai',
  platform: 'WhatsApp, Instagram, Facebook, Messenger, Web',
  icon: '🤖',
  accessType: 'web'
}, {
  id: 'amazon-rufus',
  name: 'Amazon Rufus',
  category: 'Product Research',
  description: 'Deep Q&A on products using millions of reviews; feature and model comparisons.',
  accessMethod: 'Amazon App',
  accessLink: 'https://www.amazon.com',
  platform: 'Amazon Shopping App (US)',
  icon: '📦',
  accessType: 'app'
}, {
  id: 'perplexity',
  name: 'Perplexity',
  category: 'Research-to-Purchase',
  description: 'End-to-end product research and buying flow from a single chat.',
  accessMethod: 'Web & Apps',
  accessLink: 'https://www.perplexity.ai',
  platform: 'Web, iOS, Android',
  icon: '🔍',
  accessType: 'web'
}, {
  id: 'chatgpt-shopping',
  name: 'ChatGPT',
  category: 'General Shopping / Advice',
  description: 'Multi-turn consultative research, comparisons, tradeoffs for any major category.',
  accessMethod: 'Web & Apps',
  accessLink: 'https://chat.openai.com',
  platform: 'Web, iOS, Android',
  icon: '💬',
  accessType: 'web'
}, {
  id: 'snapchat-myai',
  name: 'Snapchat My AI',
  category: 'General / Youth Lifestyle',
  description: 'Instant outfit suggestions, youth-oriented lifestyle picks, quick ideas.',
  accessMethod: 'Snapchat App',
  accessLink: 'https://www.snapchat.com',
  platform: 'Snapchat App',
  icon: '👻',
  accessType: 'app'
}];
const FASHION_STYLE: ActiveAgent[] = [{
  id: 'saks-stylist',
  name: 'Saks Fifth Avenue Text Stylist',
  category: 'Luxury Fashion / Concierge',
  description: 'Human-assisted luxury styling and sourcing specific high-end items.',
  accessMethod: 'SMS: 1-877-551-7257',
  accessLink: 'sms:1-877-551-7257',
  platform: 'SMS (USA)',
  icon: '👔',
  accessType: 'sms'
}, {
  id: 'acloset',
  name: 'Acloset',
  category: 'Personal Wardrobe Stylist',
  description: 'Creating outfits from your existing wardrobe, digital closet management, smart recommendations.',
  accessMethod: 'iOS & Android App',
  accessLink: 'https://www.acloset.app',
  platform: 'iOS, Android',
  icon: '👗',
  accessType: 'app'
}, {
  id: 'reformation',
  name: 'Reformation',
  category: 'Sustainable Fashion Concierge',
  description: 'US shoppers wanting sustainable fashion with sizing and style advice.',
  accessMethod: 'SMS: 1-855-752-1925',
  accessLink: 'sms:1-855-752-1925',
  platform: 'SMS (USA), Web',
  icon: '🌿',
  accessType: 'sms'
}, {
  id: 'daydream',
  name: 'Daydream',
  category: 'AI Fashion Discovery',
  description: 'Screenshot-based shopping, outfit discovery, finding similar items across many brands.',
  accessMethod: 'iOS App',
  accessLink: 'https://www.trydaydream.com',
  platform: 'iOS, Web',
  icon: '✨',
  accessType: 'app'
}, {
  id: 'alta',
  name: 'Alta AI',
  category: 'Personal Stylist & Closet Analytics',
  description: 'Daily outfits, cost-per-wear planning, wardrobe optimization and style evolution.',
  accessMethod: 'iOS App',
  accessLink: 'https://www.styledbyalta.com',
  platform: 'iOS, Web',
  icon: '👚',
  accessType: 'app'
}];
const TRAVEL_FLIGHTS: ActiveAgent[] = [{
  id: 'guidegeek',
  name: 'GuideGeek',
  category: 'Travel Planner / AI Guide',
  description: 'Full trip planning, local tips (food, safety, customs), 24/7 travel Q&A.',
  accessMethod: 'WhatsApp',
  accessLink: 'https://wa.me/15074706507',
  platform: 'WhatsApp',
  icon: '✈️',
  accessType: 'whatsapp'
}, {
  id: 'eddy-travels',
  name: 'Eddy Travels',
  category: 'Flight & Hotel Search',
  description: 'Quick flight/hotel price comparisons and booking links worldwide.',
  accessMethod: 'Telegram',
  accessLink: 'https://t.me/eddytravels_bot',
  platform: 'Telegram',
  icon: '🌍',
  accessType: 'telegram'
}, {
  id: 'airtrack',
  name: 'AirTrackBot',
  category: 'Flight Price Tracking',
  description: 'Setting alerts for specific routes and being notified when prices drop under your target.',
  accessMethod: 'Telegram',
  accessLink: 'https://t.me/airtrack_bot',
  platform: 'Telegram',
  icon: '🛫',
  accessType: 'telegram'
}];
const GROCERY_FOOD: ActiveAgent[] = [{
  id: 'walmart-text',
  name: 'Walmart "Text to Shop"',
  category: 'Grocery Reordering',
  description: 'Fast reordering of favorite groceries and household essentials via text.',
  accessMethod: 'SMS: 397737',
  accessLink: 'sms:397737',
  platform: 'SMS (USA)',
  icon: '🛒',
  accessType: 'sms'
}, {
  id: 'maggi-chef',
  name: 'Maggi Chef',
  category: 'Recipe Assistant',
  description: 'Generating recipes from ingredients you already have at home.',
  accessMethod: 'WhatsApp',
  accessLink: 'https://wa.me/919289722997',
  platform: 'WhatsApp',
  icon: '👨‍🍳',
  accessType: 'whatsapp'
}, {
  id: 'hellmanns',
  name: 'Hellmann\'s "Meal Reveal"',
  category: 'Food Waste / Recipe Tool',
  description: 'Turning leftovers and fridge items into meal ideas to reduce waste.',
  accessMethod: 'Web Tool',
  accessLink: 'https://www.hellmanns.com/us/en/meal-reveal.html',
  platform: 'Web',
  icon: '🥗',
  accessType: 'web'
}];
const VOICE_ELECTRONICS: ActiveAgent[] = [{
  id: 'bestbuy-sms',
  name: 'Best Buy SMS Support',
  category: 'Electronics / Stock Check',
  description: 'Checking product specs, local store stock, and basic support for electronics.',
  accessMethod: 'SMS: 1-888-237-8289',
  accessLink: 'sms:1-888-237-8289',
  platform: 'SMS (USA)',
  icon: '💻',
  accessType: 'sms'
}, {
  id: 'alexa-voice',
  name: 'Amazon Alexa',
  category: 'Voice Shopping',
  description: 'Hands-free reordering, quick price checks and simple product discovery on Amazon.',
  accessMethod: 'Alexa App',
  accessLink: 'https://www.amazon.com/alexa',
  platform: 'Alexa App, Echo devices',
  icon: '🔊',
  accessType: 'app'
}, {
  id: 'chatgpt-voice',
  name: 'ChatGPT (Voice Mode)',
  category: 'Voice Shopping / Consult',
  description: 'Complex, multi-turn product research and advice by voice (TVs, laptops, appliances, fashion).',
  accessMethod: 'ChatGPT App',
  accessLink: 'https://chat.openai.com',
  platform: 'ChatGPT App (iOS, Android)',
  icon: '🎙️',
  accessType: 'app'
}, {
  id: 'dealblast',
  name: 'DealBlast',
  category: 'Deal Notifications',
  description: 'Real-time alerts for deep discounts, price mistakes, and flash deals at major retailers.',
  accessMethod: 'Telegram',
  accessLink: 'https://t.me/DealBlast',
  platform: 'Telegram',
  icon: '🔥',
  accessType: 'telegram'
}];
function AgentCard({
  agent
}: {
  agent: ActiveAgent;
}) {
  const getAccessIcon = () => {
    switch (agent.accessType) {
      case 'sms':
        return <Phone size={16} className="text-green-600" data-id="element-38" />;
      case 'whatsapp':
        return <MessageCircle size={16} className="text-green-600" data-id="element-39" />;
      case 'telegram':
        return <MessageCircle size={16} className="text-blue-600" data-id="element-40" />;
      case 'web':
        return <ExternalLink size={16} className="text-slate-600" data-id="element-41" />;
      case 'app':
        return <Zap size={16} className="text-purple-600" data-id="element-42" />;
      default:
        return null;
    }
  };
  return <a href={agent.accessLink} target="_blank" rel="noopener noreferrer" className="flex-shrink-0 w-80 rounded-2xl bg-white border border-slate-200 hover:border-slate-300 shadow-sm hover:shadow-md transition-all p-5 group" data-id="element-43">
      <div className="flex items-start gap-3 mb-3" data-id="element-44">
        <div className="text-3xl" data-id="element-45">{agent.icon}</div>
        <div className="flex-1 min-w-0" data-id="element-46">
          <h3 className="font-bold text-base text-slate-900 mb-1 group-hover:text-slate-700 line-clamp-1" data-id="element-47">
            {agent.name}
          </h3>
          <p className="text-xs text-slate-500 font-medium mb-2" data-id="element-48">
            {agent.category}
          </p>
        </div>
      </div>

      <p className="text-sm text-slate-700 leading-relaxed mb-3 line-clamp-2" data-id="element-49">
        {agent.description}
      </p>

      <div className="flex items-center justify-between gap-2 pt-3 border-t border-slate-100" data-id="element-50">
        <div className="flex items-center gap-1.5 text-xs text-slate-600" data-id="element-51">
          {getAccessIcon()}
          <span className="font-medium" data-id="element-52">{agent.accessMethod}</span>
        </div>
      </div>

      <div className="mt-2" data-id="element-53">
        <span className="text-xs text-slate-500" data-id="element-54">{agent.platform}</span>
      </div>
    </a>;
}
function AgentSection({
  title,
  agents,
  icon
}: {
  title: string;
  agents: ActiveAgent[];
  icon: string;
}) {
  return <div className="mb-8" data-id="element-55">
      <div className="flex items-center gap-2 mb-4" data-id="element-56">
        <span className="text-2xl" data-id="element-57">{icon}</span>
        <h3 className="text-lg font-bold text-slate-900" data-id="element-58">{title}</h3>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide" data-id="element-59">
        {agents.map(agent => <AgentCard key={agent.id} agent={agent} data-id="element-60" />)}
      </div>
    </div>;
}
export function ActiveAgents() {
  return <div className="mb-16" data-id="element-61">
      <div className="flex items-center gap-3 mb-6" data-id="element-62">
        <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 grid place-items-center" data-id="element-63">
          <Zap size={20} className="text-white" data-id="element-64" />
        </div>
        <div data-id="element-65">
          <h2 className="text-2xl font-bold" data-id="element-66">Active AI Shopping Agents</h2>
          <p className="text-sm text-slate-600" data-id="element-67">
            Live agents you can text, chat, or talk to right now for instant
            shopping help
          </p>
        </div>
      </div>

      <AgentSection title="Super-Agents" agents={SUPER_AGENTS} icon="⚡" data-id="element-68" />
      <AgentSection title="Fashion & Style" agents={FASHION_STYLE} icon="👗" data-id="element-69" />
      <AgentSection title="Travel & Flights" agents={TRAVEL_FLIGHTS} icon="✈️" data-id="element-70" />
      <AgentSection title="Grocery, Food & Home" agents={GROCERY_FOOD} icon="🛒" data-id="element-71" />
      <AgentSection title="Voice, Electronics & Deals" agents={VOICE_ELECTRONICS} icon="🔊" data-id="element-72" />

      <style data-id="element-73">{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>;
}